import { useEffect, useRef, type MouseEvent } from 'react'
import { type BusinessEngagementEvent, type PageType } from './constants.ts'

/**
 * Client-side listing engagement tracking.
 *
 * These helpers send ONE thing: a first-party beacon to
 * `/resources/business-event`. That route is the single writer for both the
 * `BusinessMetricDay` counters and the PostHog event.
 *
 * They deliberately do not call `trackClient` as well. Emitting from both sides
 * puts two copies of the same interaction in PostHog with different
 * `$insert_id`s, so nothing dedupes them and every funnel step reads roughly
 * double — the same defect that inflated ad CTR. One writer, server-side, is
 * also the copy that survives ad blockers, which is what the owner-facing
 * numbers are built on.
 */

/**
 * Only fields the client is the authority on.
 *
 * Anything the server can look up from the listing — name, slug, featured
 * status, owner — is read from the database in the beacon route instead of being
 * accepted from the page. That keeps the event stream un-spoofable and keeps
 * this payload small enough for `sendBeacon`.
 */
export interface ListingEventContext {
	businessId: string
	/** Where on the page the interaction happened: 'header', 'sticky_cta', 'card' */
	placement?: string
	pageType?: PageType
	categorySlug?: string
	citySlug?: string
	position?: number
}

const BEACON_URL = '/resources/business-event'

/**
 * Fire the first-party beacon.
 *
 * `sendBeacon` is queued by the browser and delivered even if the document is
 * being torn down by an outbound navigation, which is exactly the case for
 * tap-to-call and website clicks. `fetch` with `keepalive` is the fallback.
 */
function sendBeacon(event: BusinessEngagementEvent, ctx: ListingEventContext) {
	if (typeof window === 'undefined') return

	const body = new FormData()
	body.append('businessId', ctx.businessId)
	body.append('event', event)
	if (ctx.placement) body.append('placement', ctx.placement)
	if (ctx.pageType) body.append('pageType', ctx.pageType)
	if (ctx.categorySlug) body.append('categorySlug', ctx.categorySlug)
	if (ctx.citySlug) body.append('citySlug', ctx.citySlug)
	if (ctx.position != null) body.append('position', String(ctx.position))
	// businessName is intentionally not sent: the server reads it from the
	// listing it just looked up, so a client cannot relabel someone else's
	// business in our analytics.

	try {
		if (navigator.sendBeacon?.(BEACON_URL, body)) return
	} catch {
		// fall through to fetch
	}

	void fetch(BEACON_URL, { method: 'POST', body, keepalive: true }).catch(
		() => {},
	)
}

/**
 * Record a listing engagement event.
 *
 * The beacon is the whole implementation on purpose — see the module comment.
 * The server attaches the PostHog group, the listing's slug/featured status, and
 * the visitor's distinct id from the PostHog cookie, so nothing is lost by not
 * capturing here.
 */
export function trackListingEvent(
	event: BusinessEngagementEvent,
	ctx: ListingEventContext,
) {
	sendBeacon(event, ctx)
}

/**
 * Record a profile view, once per business per browser session.
 *
 * Deduping matters because an owner reads "views" as "people", and React Router
 * remounts this component on every client-side navigation back to the page.
 */
export function useBusinessProfileView(ctx: ListingEventContext) {
	const { businessId } = ctx
	// Keep the latest context without making the effect depend on object identity.
	const ctxRef = useRef(ctx)
	ctxRef.current = ctx

	useEffect(() => {
		if (typeof window === 'undefined') return

		const key = `dfw_pv:${businessId}`
		try {
			if (window.sessionStorage.getItem(key)) return
			window.sessionStorage.setItem(key, '1')
		} catch {
			// Private mode / storage disabled — count it rather than lose it.
		}

		trackListingEvent('business_profile_viewed', ctxRef.current)
	}, [businessId])
}

/**
 * Props for an outbound contact link (phone, website, directions).
 * Returns the handler to spread onto the anchor.
 *
 * For a *result* click, prefer `<BusinessListing track={...} />`, which calls
 * this internally. Spread it by hand only on a surface that renders its own
 * custom link markup — currently `/best/{category}` and `/{city}/{category}`.
 *
 * If either of those migrates to `BusinessListing`, delete the hand-rolled
 * spread at the same time. Keeping both would fire `listing_result_clicked`
 * twice per click with different `$insert_id`s, so nothing dedupes them and
 * every result-click number silently doubles.
 */
export function listingLinkHandler(
	event: BusinessEngagementEvent,
	ctx: ListingEventContext,
) {
	return {
		onClick: () => trackListingEvent(event, ctx),
		// Keyboard activation of a link fires click, but middle-click and
		// context-menu "open in new tab" do not — auxclick covers the former.
		onAuxClick: (e: MouseEvent) => {
			if (e.button === 1) trackListingEvent(event, ctx)
		},
	}
}
